import * as Sentry from "@sentry/react";

type ObservabilityFeature =
  | "onboarding"
  | "heatmaps"
  | "funnels"
  | "experiments"
  | "ai_chat"
  | "attribution"
  | "connectors"
  | "api_access"
  | "admin"
  | "analytics"
  | "paywall"
  | "billing";

type ObservabilityLevel = "info" | "warn" | "error";

/**
 * Attributes shared by every log and issue. `property_id` is the axis the team
 * filters on in Sentry to see what a single customer property is doing.
 */
export type ObservabilityScope = {
  propertyId?: string | null;
  domain?: string | null;
  workspaceId?: string | null;
  planTier?: string | null;
  userId?: string | null;
  email?: string | null;
};

type Attributes = Record<string, string | number | boolean | null | undefined>;

const BREADCRUMB_LEVEL: Record<ObservabilityLevel, Sentry.SeverityLevel> = {
  info: "info",
  warn: "warning",
  error: "error",
};

const hasReportedThisSession = (key: string) => {
  try {
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, "1");
  } catch {
    return false;
  }

  return false;
};

export const getErrorBody = (error: unknown) => {
  if (!error || typeof error !== "object") return null;

  const maybeApiError = error as {
    body?: unknown;
    response?: { data?: unknown };
  };

  return maybeApiError.body ?? maybeApiError.response?.data ?? null;
};

export const getBodyCode = (body: unknown) => {
  if (!body || typeof body !== "object") return null;

  const data = body as Record<string, unknown>;
  return typeof data.error === "string"
    ? data.error
    : typeof data.code === "string"
    ? data.code
    : null;
};

const FETCH_NETWORK_FAILURE = /failed to fetch|networkerror|load failed/i;

/**
 * A response-less browser/network failure (offline, DNS, CORS, aborted fetch)
 * as opposed to a server error that carries an HTTP status. These are commonly
 * transient client conditions, so callers log them as degraded loads rather
 * than opening a Sentry Issue.
 */
export const isNetworkFailure = (error: unknown) => {
  if (getHttpStatus(error) !== null || !(error instanceof Error)) return false;
  if (error.name === "AbortError" || error.name === "NetworkError") return true;

  return error instanceof TypeError && FETCH_NETWORK_FAILURE.test(error.message);
};

export const getHttpStatus = (error: unknown): number | null => {
  if (!error || typeof error !== "object") return null;

  const maybeApiError = error as {
    status?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown };
  };

  const candidate =
    maybeApiError.status ?? maybeApiError.statusCode ?? maybeApiError.response?.status;

  return typeof candidate === "number" ? candidate : null;
};

export const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  const code = getBodyCode(getErrorBody(error));
  return code ?? "Unknown error";
};

const scopeAttributes = (scope?: ObservabilityScope): Attributes => ({
  property_id: scope?.propertyId ?? null,
  domain: scope?.domain ?? null,
  workspace_id: scope?.workspaceId ?? null,
  plan_tier: scope?.planTier ?? null,
});

type LogFeatureEventParams = {
  feature: ObservabilityFeature;
  event: string;
  level?: ObservabilityLevel;
  message?: string;
  scope?: ObservabilityScope;
  attributes?: Attributes;
  /** When set, the event is emitted at most once per browser session. */
  dedupKey?: string;
};

/**
 * Behaviour tracking + expected-but-notable states. Emits a Sentry structured
 * Log (searchable by attributes) plus a breadcrumb for context on a later
 * error. Never creates a Sentry Issue.
 */
export const logFeatureEvent = ({
  feature,
  event,
  level = "info",
  message,
  scope,
  attributes,
  dedupKey,
}: LogFeatureEventParams) => {
  if (dedupKey && hasReportedThisSession(`humblytics:observability:${feature}:${dedupKey}`)) {
    return;
  }

  const text = message ?? `${feature}: ${event}`;
  const payload: Attributes = {
    feature,
    [`${feature}_event`]: event,
    ...scopeAttributes(scope),
    ...attributes,
  };

  Sentry.addBreadcrumb({
    category: feature,
    message: text,
    level: BREADCRUMB_LEVEL[level],
    data: payload,
  });

  Sentry.logger[level](text, payload);
};

type ReportFeatureIssueParams = {
  feature: ObservabilityFeature;
  error: unknown;
  action: string;
  message?: string;
  scope?: ObservabilityScope;
  tags?: Record<string, string | undefined>;
  context?: Record<string, unknown>;
  /** When set, the issue is captured at most once per browser session. */
  dedupKey?: string;
};

/**
 * Genuine defects only — anything that should page or triage as a bug. Creates
 * a Sentry Issue. Expected states belong in `logFeatureEvent`.
 */
export const reportFeatureIssue = ({
  feature,
  error,
  action,
  message,
  scope,
  tags,
  context,
  dedupKey,
}: ReportFeatureIssueParams) => {
  if (dedupKey && hasReportedThisSession(`humblytics:observability:${feature}:${dedupKey}`)) {
    return;
  }

  const status = getHttpStatus(error);

  Sentry.withScope((sentryScope) => {
    sentryScope.setLevel("error");

    if (scope?.userId || scope?.email) {
      sentryScope.setUser({ id: scope.userId ?? undefined, email: scope.email ?? undefined });
    }

    sentryScope.setTags({
      feature,
      [`${feature}_action`]: action,
      ...(status ? { http_status: String(status) } : {}),
      ...tags,
    });

    sentryScope.setContext(feature, {
      action,
      ...scopeAttributes(scope),
      error_message: getErrorMessage(error),
      timestamp: new Date().toISOString(),
      ...context,
    });

    Sentry.captureException(
      error instanceof Error ? error : new Error(message ?? getErrorMessage(error)),
    );
  });
};
